import React, {FC} from 'react';
import styled from 'styled-components';

import {AttachmentBase} from '../../../../../../../../../src/components/inboxes/viewer/activity/attachments/attachmentBase';
import {downloadFileFromUrl} from '../../../../../../../../../src/helpers/fileHelpers';
import {
  AttachmentStatusesEnum,
  parseVisualContentType,
} from '../../../../../../../../../src/models/api/attachmentModel';
import {
  DownloadAttachmentOptions,
  PreviewAttachmentOptions,
} from '../../../../../../../../../src/services/browser';
import {
  downloadItem,
  ElectronDownloadItemTypesEnum,
} from '../../../../../../../../../src/services/electron/electronBridgeOutbound';
import {isElectron} from '../../../../../../../../shared/helpers/src/environment/platformHelpers';

/*
 * Props.
 */

export interface AccountsCsvImportFileAttachmentProps {
  name: string;
  size: number;
  contentType: string;
  url?: string;
  file?: File;
  generateFileAsync?: () => Promise<File | undefined>;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.alphas.black40};
  background-color: ${(p) => p.theme.foreground};
  &:hover {
    background-color: ${(p) => p.theme.greys.shade20};
  }
`;

/*
 * Component.
 */

export const AccountsCsvImportFileAttachment: FC<AccountsCsvImportFileAttachmentProps> = ({
  name,
  size,
  contentType,
  url,
  file,
  generateFileAsync,
}) => {
  const handleClickAsync = async (
    fileHandler: (file: File, name: string) => Promise<void>,
    urlHandler: (url: string, name: string) => Promise<void>,
    options: DownloadAttachmentOptions | PreviewAttachmentOptions,
  ): Promise<void> => {
    const {onProgress} = options;
    if (onProgress) {
      onProgress(0.5);
    }
    // generate file on download click
    if (generateFileAsync) {
      const generatedFile = await generateFileAsync();
      if (!generatedFile) {
        throw new Error('No generated file to download');
      }
      fileHandler(generatedFile, name);
    } else if (url) {
      urlHandler(url, name);
    } else if (file) {
      fileHandler(file, name);
    }
    if (onProgress) {
      onProgress(1);
    }
  };

  return (
    <StyledDiv>
      <AttachmentBase
        onDownloadClickAsync={async (options) => {
          await handleClickAsync(downloadFile, downloadFromUrl, options);
          return {};
        }}
        onPreviewClickAsync={async (options) => handleClickAsync(previewFile, previewFromUrl, options)}
        attachment={{
          uid: 'accounts-csv-import-file-attachment',
          cid: 'accounts-csv-import-file-attachment',
          name,
          size,
          contentType,
          url: url ?? '',
          visualContentType: parseVisualContentType(contentType),
          status: AttachmentStatusesEnum.UPLOADED,
          isInline: false,
          hasThumbnail: false,
          giphyId: undefined,
          attachedAt: undefined,
          linkId: undefined,
        }}
      />
    </StyledDiv>
  );
};

/*
 * Helpers.
 */

async function downloadFile(file: File, name: string) {
  const objectUrl = URL.createObjectURL(file);
  try {
    await downloadFromUrl(objectUrl, name);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function downloadFromUrl(url: string, name: string) {
  if (!isElectron()) {
    await downloadFileFromUrl(document, url, name);
  } else {
    downloadItem(url, ElectronDownloadItemTypesEnum.DOWNLOAD, name);
  }
}

async function previewFile(file: File, name: string) {
  const objectUrl = URL.createObjectURL(file);
  try {
    previewFromUrl(objectUrl, name);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function previewFromUrl(url: string, name: string) {
  if (!isElectron()) {
    await downloadFileFromUrl(document, url, name);
  } else {
    downloadItem(url, ElectronDownloadItemTypesEnum.PREVIEW, name);
  }
}
